// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import myw from 'myWorld/base/core';
import 'myWorld/base/util';
import 'myWorld/controls/webcamDialog';
import './stringFieldEditor';
import { ImageDimensionParser } from './imageDimensionParser';
import { LightweightBase64Reader } from './lightweightBase64Reader';

/**
 * Editor for fields of type image<br/>
 * Renders a image file browser button, a thumbnail to show the selected image and the selected image size
 * @name ImageFieldEditor
 * @constructor
 * @extends {StringFieldEditor}
 */
myw.ImageFieldEditor = myw.StringFieldEditor.extend({
    tagName: 'div',
    className: 'image-input',
    events: {
        'click .field-edit-btn': 'browseFiles',
        'click .thumb-clear': 'clear'
    },
    initialize(owner, feature, fieldDD, options) {
        myw.FieldEditor.prototype.initialize.call(this, owner, feature, fieldDD, options);
        this.imageBlob = this.fieldValue; //this caches the image info that is or needs to be stored in the db.
        $('<input/>', { type: 'file', accept: 'image/*' }).appendTo(this.$el); //hidden via css
        this.photoButton = $('<button/>', { class: 'field-edit-btn' })
            .text(this.msg('add_image'))
            .button()
            .appendTo(this.$el);

        this.thumbnailImage = $('<img/>', { class: 'thumb' });
        this.fileSizeIndicator = $('<span/>', { class: 'thumb-file-size' });

        this.thumbnail = $('<div/>', { class: 'thumb-container' })
            .append(this.thumbnailImage)
            .append(this.fileSizeIndicator)
            .append($('<span/>', { class: 'thumb-clear' }))
            .appendTo(this.$el);

        if (this.fieldValue) {
            // show the value in a thumbnail and its size next to it
            const imageFormat = myw.Util.getImageFormatFor(this.fieldValue);
            this.thumbnailImage.attr('src', `data:image/${imageFormat};base64,${this.fieldValue}`);
            this.fileSizeIndicator.text(
                this.msg('image_size', {
                    size: this.feature.displayValues[this.fieldDD.internal_name]
                })
            );
            this.toggleThumbnail(true);
        }

        this.el.onchange = event => {
            // Get a reference to the taken picture or chosen image
            const files = event.target.files;
            if (files && files.length > 0) {
                this.readFileData(files[0]);
            }
        };
    },
    /**
     * Passes on the click event to the file input element to launch the file browser/camera
     */
    browseFiles() {
        // Determine if its IE 11 or under
        const isMsie =
                window.navigator.userAgent.indexOf('MSIE ') > 0 ||
                !!navigator.userAgent.match(/Trident.*rv\:11\./),
            isWindows = navigator.appVersion.indexOf('Win') != -1;

        if (!(window.File && window.FileReader)) {
            // Targets IE9 which does not support either the HTML5 input[type=file] or the getUserMedia() api
            alert(this.msg('browser_doesnt_support_image_upload'));
        } else if (!isMsie && isWindows) {
            // Targets Windows platform (but not IE browser)

            //In the case of windows devices, the HTML5 input[type=file] button doesn't launch the webcam
            //so we need to create our own actionChooser dialog which can route to our own webcam dialog
            if (!this.actionChooser) this.createActionChooser();
            this.actionChooser.dialog('open');
        } else {
            // For Android, iOS and IE9-11 on Windows
            this.$el.find('input').click();
        }
    },
    /**
     * Creates photo upload option dialog to pick between Camera and Browse options
     */
    createActionChooser() {
        this.webcam = new myw.WebcamDialog(this, {
            callback: imageData => {
                //  Convert the base64 image into an img and then process it
                const img = new Image();
                img.onload = () => {
                    this._resizeAndRotateImage(img, img.width, img.height, null).then(
                        this._displayImage.bind(this)
                    );
                };
                img.src = imageData;
            }
        });

        this.actionChooser = $('<ul>', { class: 'photo_upload_options_dialog noStyleList' }).dialog(
            {
                modal: true,
                autoOpen: false,
                width: 'auto',
                resizable: false,
                position: { my: 'center', at: 'top+160', of: window },
                title: this.msg('choose_photo_upload_option_title')
            }
        );

        $('<li><img src="images/camera.svg"/></li>')
            .append(this.msg('camera'))
            .on('click', () => {
                this.webcam.open();
                this.actionChooser.dialog('close');
            })
            .appendTo(this.actionChooser);

        $('<li><img src="images/folder.svg"/></li>')
            .append(this.msg('files'))
            .on('click', () => {
                this.$el.find('input').click(); // click the input[type=file] to launch the file browser
                this.actionChooser.dialog('close');
            })
            .appendTo(this.actionChooser);
    },
    /**
     * Clears the selected image from the image editor
     */
    clear() {
        this.toggleThumbnail(false);
        this.imageBlob = null;
    },
    /**
     * Show/Hide the thumbnail
     * @param  {boolean} show Whether to show the thumbnail or hide it
     */
    toggleThumbnail(show) {
        this.thumbnail.css('display', show ? 'inline-block' : 'none');
        this.photoButton.button(
            'option',
            'label',
            show ? this.msg('update_image') : this.msg('add_image')
        );
    },
    /**
     * Reads the browsed image file as dataURL
     * @param  {object} file File object from the browser's file api
     */
    async readFileData(file) {
        //  Extract the width, height and any EXIF Orientation info
        const imageDimensionParser = new ImageDimensionParser();
        const dimensions = await imageDimensionParser.getDimensions(file);
        if (dimensions !== null) {
            var img = new Image();
            img.onload = () => {
                //  Free up memory by revoking the object URL
                URL.revokeObjectURL(img.src);
                this._resizeAndRotateImage(
                    img,
                    dimensions.width,
                    dimensions.height,
                    dimensions.exif
                ).then(this._displayImage.bind(this));
            };
            img.onerror = e => {
                console.log('Unable to load image');
            };
            //  Create a URL for the file, is much lighter than getting the base 64
            img.src = URL.createObjectURL(file);
        } else {
            //ENH: Inform the user that the file format is not accepted
        }
    },

    async _toDataUrl(canvas) {
        return new Promise(async (resolve, reject) => {
            const type = 'image/jpeg';
            //  In some browsers, canvas.toBlob isn't available. Handle that scenario here
            if (canvas.toBlob) {
                canvas.toBlob(async blob => {
                    //  Check if the canvas is too big to process
                    if (blob == null) {
                        resolve(null);
                        return;
                    }

                    const lightweightReader = new LightweightBase64Reader();
                    const base64 = await lightweightReader.readFile(blob);
                    //  Return the blob as well for use in the thumbnail
                    resolve({ blob, base64, type });
                }, type);
            } else {
                //  Get the base64 of the data here, and strip the data: tag from the front
                const base64Data = canvas
                    .toDataURL(type)
                    .replace(/^data:image\/(png|jpg|jpeg);base64,/, '');
                resolve({ blob: null, base64: base64Data, type: type });
            }
        });
    },

    /*
     * Re-sample the image to fit the maxWidth/maxHeight bounds defined in the dd type</br>
     * If no bounds are defined, the image is used as it is</br>
     * Converts it to jpeg</br>
     * Displays the image thumbnail and the re-sampled file size</br>
     * @param  {string} imageFile  Image file read as data url</br>
     * @return {promise} Resolved to image URL;
     */
    _resizeAndRotateImage(img, width, height, exif) {
        const canvas = document.createElement('canvas'),
            ctx = canvas.getContext('2d'),
            maxDimensions = this.fieldDD.type.slice(6, -1).split(','),
            max_width = parseInt(maxDimensions[0], 10),
            max_height = parseInt(maxDimensions[1], 10);
        return new Promise(async (resolve, reject) => {
            //  Recently, WebView changed to automatically rotate uploaded images based on it's EXIF Orientation value.
            //  Test this here and change our functionality appropriately
            if (!this._browserAutoRotatesPromise) {
                this._browserAutoRotatesPromise = new Promise(function(resolve) {
                    //  We adapted this check from the Modernizr framework
                    //  https://github.com/Modernizr/Modernizr/blob/1560f1f0ddd159209c2c97a84ca49b727b2e7673/feature-detects/exif-orientation.js
                    const img = new Image();
                    img.onerror = function() {
                        resolve(false);
                    };
                    img.onload = function() {
                        resolve(img.width !== 2);
                    };
                    // There may be a way to shrink this more, it's a 1x2 white jpg with the orientation flag set to 6
                    img.src =
                        'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEAYABgAAD/4QAiRXhpZgAASUkqAAgAAAABABIBAwABAAAABgASAAAAAAD/2wBDAAEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQH/2wBDAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQH/wAARCAABAAIDASIAAhEBAxEB/8QAHwAAAQUBAQEBAQEAAAAAAAAAAAECAwQFBgcICQoL/8QAtRAAAgEDAwIEAwUFBAQAAAF9AQIDAAQRBRIhMUEGE1FhByJxFDKBkaEII0KxwRVS0fAkM2JyggkKFhcYGRolJicoKSo0NTY3ODk6Q0RFRkdISUpTVFVWV1hZWmNkZWZnaGlqc3R1dnd4eXqDhIWGh4iJipKTlJWWl5iZmqKjpKWmp6ipqrKztLW2t7i5usLDxMXGx8jJytLT1NXW19jZ2uHi4+Tl5ufo6erx8vP09fb3+Pn6/8QAHwEAAwEBAQEBAQEBAQAAAAAAAAECAwQFBgcICQoL/8QAtREAAgECBAQDBAcFBAQAAQJ3AAECAxEEBSExBhJBUQdhcRMiMoEIFEKRobHBCSMzUvAVYnLRChYkNOEl8RcYGRomJygpKjU2Nzg5OkNERUZHSElKU1RVVldYWVpjZGVmZ2hpanN0dXZ3eHl6goOEhYaHiImKkpOUlZaXmJmaoqOkpaanqKmqsrO0tba3uLm6wsPExcbHyMnK0tPU1dbX2Nna4uPk5ebn6Onq8vP09fb3+Pn6/9oADAMBAAIRAxEAPwD+/iiiigD/2Q==';
                });
            }
            const isAutoRotating = await this._browserAutoRotatesPromise;

            let dataUrl;
            // If the EXIF Orientation is present, use that to determine orientation, else calculate using width and height
            let isLandscape =
                isAutoRotating && exif && exif.Orientation ? exif.Orientation < 4 : width >= height;
            //  If the image has been auto-rotated, change it back here
            if (isAutoRotating && exif && exif.Orientation >= 5) {
                isLandscape = !isLandscape;
                const temp = width;
                width = height;
                height = temp;
            }
            //  This is the best case sizes, these will be reduced if the canvas is too big to handle
            let desiredWidth = width,
                desiredHeight = height;

            // Compare what to scale down the size to if needed
            if (isLandscape) {
                //Landscape
                if (width > max_width) {
                    desiredHeight *= max_width / width;
                    desiredWidth = max_width;
                }
            } else {
                //Portrait
                if (height > max_height) {
                    desiredWidth *= max_height / height;
                    desiredHeight = max_height;
                }
            }
            canvas.width = desiredWidth;
            canvas.height = desiredHeight;
            //  Keep trying to draw to the canvas, in event of a failure, scale down the canvas and try again
            let valid = false;
            let scale = 1.0;
            while (!valid) {
                canvas.width = desiredWidth * scale;
                canvas.height = desiredHeight * scale;
                //  Reset to identity matrix
                ctx.setTransform(1, 0, 0, 1, 0, 0);
                if (!isAutoRotating) this._rotateCanvas(exif, ctx, canvas.width, canvas.height);
                //  This will return null or throw an error if the canvas is too big to process
                try {
                    ctx.drawImage(img, 0, 0, width, height, 0, 0, canvas.width, canvas.height);
                    dataUrl = await this._toDataUrl(canvas);
                    valid = dataUrl && dataUrl.base64 !== 'data:,';
                    scale -= 0.05;
                } catch (error) {
                    valid = false;
                    scale -= 0.05;
                }

                if (scale <= 0) {
                    reject(new Error('Unable to process image'));
                    return;
                }
            }

            resolve(dataUrl);
        });
    },
    getValue() {
        return this.imageBlob || null;
    },

    //display image and size in thumbnail when the image is loaded
    _displayImage(info) {
        //Assign the image source to the dummy image element and the thumbnail
        if (info.blob) {
            const url = URL.createObjectURL(info.blob);
            this.thumbnailImage.attr('src', url);
            //  Once the info is loaded into the image, we don't need the oject anymore, get rid of it here to reduce memory
            this.thumbnailImage.one('load', () => {
                URL.revokeObjectURL(url);
            });
        } else {
            //  Reconstruct the full base64 string if the file blob isn't available
            this.thumbnailImage.attr('src', 'data:' + info.type + ';base64,' + info.base64);
        }
        this.imageBlob = info.base64;

        const baseSize = info.blob ? info.blob.size : (info.base64.length * 3) / 4;
        const fileSize = parseInt(baseSize / 1024, 10); //image size in KB

        this.fileSizeIndicator.text(this.msg('image_size', { size: fileSize }));
        this.toggleThumbnail(true);

        this._changed();
    },

    _rotateCanvas(exif, ctx, width, height) {
        if (exif) {
            switch (exif.Orientation) {
                case 2:
                    // horizontal flip
                    ctx.translate(width, 0);
                    ctx.scale(-1, 1);
                    break;
                case 3:
                    // 180 rotate left
                    ctx.translate(width, height);
                    ctx.rotate(Math.PI);
                    break;
                case 4:
                    // vertical flip
                    ctx.translate(0, height);
                    ctx.scale(1, -1);
                    break;
                case 5:
                    // vertical flip + 90 rotate right
                    ctx.rotate(0.5 * Math.PI);
                    ctx.scale(1, -1);
                    break;
                case 6:
                    // 90 rotate right
                    ctx.rotate(0.5 * Math.PI);
                    ctx.translate(0, -height);
                    break;
                case 7:
                    // horizontal flip + 90 rotate right
                    ctx.rotate(0.5 * Math.PI);
                    ctx.translate(width, -height);
                    ctx.scale(-1, 1);
                    break;
                case 8:
                    // 90 rotate left
                    ctx.rotate(-0.5 * Math.PI);
                    ctx.translate(-width, 0);
                    break;
            }
        }
    }
});

export default myw.ImageFieldEditor;
